import { useCallback, useEffect, useRef, useState } from 'react'
import { roomRequest } from './roomApi'
import { assignedTaskSummary, localDateKey } from './taskReminders'
import type { NotificationPage } from './types'

const empty: NotificationPage = {
  items: [],
  next: null,
  unreadCount: 0,
  readThrough: 0,
  assignedTasks: [],
}

export function useRoomNotifications(userId: string) {
  const [data, setData] = useState<NotificationPage>(empty)
  const [loaded, setLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [mutating, setMutating] = useState(false)
  const [error, setError] = useState('')
  const [today, setToday] = useState(localDateKey)
  const requestVersion = useRef(0)
  const pages = useRef(1)

  const refresh = useCallback(async () => {
    const version = ++requestVersion.current
    setLoading(true)
    try {
      const first = await roomRequest<NotificationPage>(
        userId,
        '/api/notifications',
      )
      const items = [...first.items]
      let next = first.next
      for (let page = 1; page < pages.current && next !== null; page++) {
        if (version !== requestVersion.current) return false
        const older = await roomRequest<NotificationPage>(
          userId,
          '/api/notifications?before=' + next,
        )
        items.push(...older.items)
        next = older.next
      }
      if (version !== requestVersion.current) return false
      setData({ ...first, items, next })
      setToday(localDateKey())
      setLoaded(true)
      setError('')
      return true
    } catch (error) {
      if (version === requestVersion.current)
        setError(
          error instanceof Error
            ? error.message
            : 'Updates could not be refreshed.',
        )
      return false
    } finally {
      if (version === requestVersion.current) setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    void refresh()
    const whenVisible = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    const timer = window.setInterval(whenVisible, 20_000)
    window.addEventListener('focus', whenVisible)
    window.addEventListener('bramble:remote-change', whenVisible)
    window.addEventListener('bramble:tasks-changed', whenVisible)
    document.addEventListener('visibilitychange', whenVisible)
    return () => {
      ++requestVersion.current
      window.clearInterval(timer)
      window.removeEventListener('focus', whenVisible)
      window.removeEventListener('bramble:remote-change', whenVisible)
      window.removeEventListener('bramble:tasks-changed', whenVisible)
      document.removeEventListener('visibilitychange', whenVisible)
    }
  }, [refresh])

  const markRead = async (id?: string) => {
    setMutating(true)
    setError('')
    try {
      await roomRequest(
        userId,
        '/api/notifications/read',
        'PATCH',
        id ? { id } : { through: data.readThrough },
      )
      // Read state is authoritative on the server, shared across this account's browsers.
      return await refresh()
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : 'Could not mark these updates as read.',
      )
      return false
    } finally {
      setMutating(false)
    }
  }
  const loadMore = async () => {
    if (loading || data.next === null) return
    pages.current++
    if (!(await refresh())) pages.current--
  }
  return {
    ...data,
    loaded,
    loading,
    mutating,
    error,
    today,
    summary: assignedTaskSummary(data.assignedTasks, today),
    refresh,
    markRead,
    loadMore,
  }
}
